import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PlanCards } from "@/components/PlanCards";
import { SUB_STATUS, limitLabel } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CreditCard, ExternalLink } from "lucide-react";

function UsageBar({ label, used, limit }) {
  const unlimited = limit >= 100000;
  const pct = unlimited ? 8 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const over = !unlimited && used >= limit;
  return (
    <div data-testid={`usage-${label}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className={over ? "text-red-600 font-medium" : "text-gray-700"}>{used} / {limitLabel(limit)}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${over ? "bg-red-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Billing() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busyPlan, setBusyPlan] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const load = () => api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/subscription/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const checkout = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan: planKey, cycle, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "決済ページの作成に失敗しました");
      setBusyPlan(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscription/billing-portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "請求ポータルを開けませんでした");
      setPortalLoading(false);
    }
  };

  if (!sub) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  const status = SUB_STATUS[sub.subscription_status] || SUB_STATUS.active;

  return (
    <div className="animate-fade-in max-w-5xl" data-testid="billing-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-medium tracking-tight">プラン・お支払い</h1>
        <p className="text-sm text-muted-foreground mt-1">サブスクリプションと利用状況を管理します。</p>
      </div>

      <div className="bg-white border border-border rounded-sm p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">現在のプラン</div>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-display text-2xl">{sub.plan_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-sm border ${status.tone}`} data-testid="sub-status">{status.label}</span>
            </div>
            {sub.subscription_status === "trialing" && sub.trial_days_left != null && (
              <div className="text-sm text-amber-600 mt-1" data-testid="trial-days">無料トライアル残り {sub.trial_days_left} 日</div>
            )}
          </div>
          {sub.has_payment && (
            <Button variant="outline" onClick={openPortal} disabled={portalLoading} data-testid="billing-portal-btn">
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" />お支払い方法・領収書<ExternalLink className="w-3.5 h-3.5 ml-2" /></>}
            </Button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-5 pt-5 border-t border-border">
          <UsageBar label="物件数" used={sub.usage.properties} limit={sub.limits.properties} />
          <UsageBar label="ユーザー数" used={sub.usage.users} limit={sub.limits.users} />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">プランを変更</h2>
        <div className="inline-flex items-center bg-gray-100 rounded-sm p-1" data-testid="billing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="billing-cycle-monthly"
            className={`px-3 py-1 text-sm rounded-sm ${cycle === "monthly" ? "bg-white shadow-sm font-medium" : "text-muted-foreground"}`}>月払い</button>
          <button onClick={() => setCycle("yearly")} data-testid="billing-cycle-yearly"
            className={`px-3 py-1 text-sm rounded-sm ${cycle === "yearly" ? "bg-white shadow-sm font-medium" : "text-muted-foreground"}`}>年払い</button>
        </div>
      </div>
      <PlanCards plans={plans} cycle={cycle} currentPlan={sub.plan} onSelect={checkout} busyPlan={busyPlan} />
      <p className="text-xs text-muted-foreground mt-4">
        テスト環境では Stripe テストカード <code className="bg-gray-100 px-1 rounded">4242 4242 4242 4242</code>（有効期限は未来の日付、CVCは任意）で決済をお試しいただけます。
      </p>
    </div>
  );
}
